import { useState, useEffect, useCallback } from "react";
import { NOTIFICATION_TIMEOUT } from "../constants";

/**
 * Hook to manage notifications with auto-dismiss
 * @param {number} timeout - Timeout in milliseconds (default from constants)
 * @returns {Object} Object containing notification state and functions
 */
export const useNotification = (timeout = NOTIFICATION_TIMEOUT) => {
  const [notification, setNotification] = useState(null);
  const [notificationType, setNotificationType] = useState("info"); // 'info', 'success', 'error', 'warning'

  // Auto-clear notifications after timeout
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
        setNotificationType("info");
      }, timeout);
      return () => clearTimeout(timer);
    }
  }, [notification, timeout]);

  // Show a notification with optional type
  const showNotification = useCallback((message, type = "info") => {
    setNotification(message);
    setNotificationType(type);
  }, []);

  // Clear notification immediately
  const clearNotification = useCallback(() => {
    setNotification(null);
    setNotificationType("info");
  }, []);

  // Show success notification
  const showSuccess = useCallback(
    (message) => {
      showNotification(message, "success");
    },
    [showNotification],
  );

  // Show error notification
  const showError = useCallback(
    (message) => {
      showNotification(message, "error");
    },
    [showNotification],
  );

  // Show warning notification
  const showWarning = useCallback(
    (message) => {
      showNotification(message, "warning");
    },
    [showNotification],
  );

  return {
    notification,
    notificationType,
    showNotification,
    clearNotification,
    showSuccess,
    showError,
    showWarning,
  };
};
